import { ExpandedState } from '@tanstack/react-table';
import { useEffect } from 'react';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppContext } from 'src/contexts/AppContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { useCatchUpModeEventIdsMap } from 'src/hooks/api/useCacheUpModeEventIdsMap';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  ACTIVE_EVENT_IDS_2_QUERY_PARAM,
  ACTIVE_EVENT_IDS_QUERY_PARAM,
  POS_EVENT_IDS_SEPARATOR,
  TAB_EVENT_IDS_2_QUERY_PARAM,
  TAB_EVENT_IDS_MAPPING_ID,
  TAB_EVENT_IDS_QUERY_PARAM,
  TAB_EVENT_IDS_SEPARATOR,
} from 'src/navigations/Routes/InventoryEvent/InventoryEvent.constants';
import { MainRoute } from 'src/navigations/Routes/MainRoute';
import {
  CatchUpModeSource,
  CommandOptionType,
  Feature,
  GroupType,
  Listing,
  ListingGroup,
} from 'src/WebApiController';

const RouteAllowCatchup = [
  MainRoute.Discovery,
  MainRoute.DiscoveryEvent,
  MainRoute.InventoryEvent,
];

export const useCatchUpParams = (
  catchUpModeSource = CatchUpModeSource.InventoryEvents
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { mainRoute } = useAppContext();

  const posEventIds = searchParams
    .get(TAB_EVENT_IDS_QUERY_PARAM)
    ?.toLowerCase();
  const activeEventIds = searchParams
    .get(ACTIVE_EVENT_IDS_QUERY_PARAM)
    ?.toLowerCase();

  const viagVirtualIds = searchParams
    .get(TAB_EVENT_IDS_2_QUERY_PARAM)
    ?.toLowerCase();
  const activeViagVirtualId = searchParams
    .get(ACTIVE_EVENT_IDS_2_QUERY_PARAM)
    ?.toLowerCase();

  const eventIdsCacheId = searchParams.get(TAB_EVENT_IDS_MAPPING_ID);

  const { keyValueCache: eventIdsString } = useCatchUpModeEventIdsMap(
    eventIdsCacheId ?? undefined,
    catchUpModeSource,
    false
  );

  // [['event1', 'event2'], ['event3', 'event4'], ['event5']]
  const posEventIdsList = useMemo(() => {
    return posEventIds
      ?.split(TAB_EVENT_IDS_SEPARATOR)
      ?.map((eid) => eid.split(POS_EVENT_IDS_SEPARATOR));
  }, [posEventIds]);

  const viagVirtualIdsList = useMemo(() => {
    if (eventIdsString) {
      return JSON.parse(eventIdsString.eventIdsJson) as string[];
    }
    return viagVirtualIds
      ? decodeURIComponent(viagVirtualIds).split(TAB_EVENT_IDS_SEPARATOR)
      : undefined;
  }, [eventIdsString, viagVirtualIds]);

  const numberOfEvents = useMemo(
    () => viagVirtualIdsList?.length ?? posEventIdsList?.length,
    [posEventIdsList?.length, viagVirtualIdsList?.length]
  );

  const selectedIndex = useMemo(() => {
    if (activeEventIds && posEventIdsList) {
      const activePosEventIds = activeEventIds.split(POS_EVENT_IDS_SEPARATOR);
      return posEventIdsList.findIndex((posEventIds) =>
        activePosEventIds.some((posEventId) => posEventIds.includes(posEventId))
      );
    } else if (activeViagVirtualId && viagVirtualIdsList) {
      return viagVirtualIdsList.findIndex(
        (viagId) => activeViagVirtualId === viagId
      );
    }
  }, [
    activeEventIds,
    activeViagVirtualId,
    posEventIdsList,
    viagVirtualIdsList,
  ]);

  const isCatchupMode = useMemo(
    () =>
      RouteAllowCatchup.includes(mainRoute) &&
      Boolean(
        (activeEventIds && posEventIds) ||
          (activeViagVirtualId && viagVirtualIds) ||
          eventIdsCacheId
      ),
    [
      mainRoute,
      activeEventIds,
      posEventIds,
      activeViagVirtualId,
      viagVirtualIds,
      eventIdsCacheId,
    ]
  );

  const onPrevClick = useCallback(() => {
    if (
      (posEventIdsList == null && viagVirtualIdsList == null) ||
      selectedIndex == null
    ) {
      return;
    }
    const index = selectedIndex - 1;
    if (index >= 0) {
      if (posEventIdsList) {
        setSearchParams((prev) => {
          prev.set(
            ACTIVE_EVENT_IDS_QUERY_PARAM,
            posEventIdsList[index].join(POS_EVENT_IDS_SEPARATOR)
          );
          return prev;
        });
      } else if (viagVirtualIdsList) {
        setSearchParams((prev) => {
          prev.set(ACTIVE_EVENT_IDS_2_QUERY_PARAM, viagVirtualIdsList[index]);
          return prev;
        });
      }
    }
  }, [posEventIdsList, selectedIndex, setSearchParams, viagVirtualIdsList]);

  const onNextClick = useCallback(() => {
    if (
      (posEventIdsList == null && viagVirtualIdsList == null) ||
      selectedIndex == null
    ) {
      return;
    }
    const index = selectedIndex + 1;
    if (posEventIdsList) {
      if (index < posEventIdsList.length) {
        setSearchParams((prev) => {
          prev.set(
            ACTIVE_EVENT_IDS_QUERY_PARAM,
            posEventIdsList[index].join(POS_EVENT_IDS_SEPARATOR)
          );
          return prev;
        });
      }
    } else if (viagVirtualIdsList) {
      if (index < viagVirtualIdsList.length) {
        setSearchParams((prev) => {
          prev.set(ACTIVE_EVENT_IDS_2_QUERY_PARAM, viagVirtualIdsList[index]);
          return prev;
        });
      }
    }
  }, [posEventIdsList, selectedIndex, setSearchParams, viagVirtualIdsList]);

  return {
    posEventIdsList,
    viagVirtualIdsList,
    activeEventIds,
    activeViagVirtualId,
    numberOfEvents,
    selectedIndex,
    onPrevClick,
    onNextClick,
    setSearchParams,
    isCatchupMode,
  };
};

type UseExpandCollapseListingsProps = {
  listings?: Listing[];
  expandedState: ExpandedState;
  setExpandedState: React.Dispatch<React.SetStateAction<ExpandedState>>;
};

export const useExpandCollapseListings = ({
  listings,
  expandedState,
  setExpandedState,
}: UseExpandCollapseListingsProps) => {
  const hasExpandCollapseFeature = useUserHasFeature(Feature.MultiExpandGroups);

  const { isCommandMenuOpened, registerCommand, unregisterCommand } =
    useCommandMenuContext();

  const updateItemsState = useCallback(
    (expanded: boolean, includeLeader: boolean) => {
      const newState =
        listings?.reduce(
          (acc, l) => {
            if (l.isLtGrp) {
              if (includeLeader) {
                acc[l.id.toString()] = expanded;
              } else {
                acc[l.id.toString()] = (
                  expandedState as Record<string, boolean>
                )[l.id.toString()];
              }

              (l as ListingGroup).groupItems.forEach((i) => {
                const groupType = (i as ListingGroup).groupType;
                if (
                  groupType === GroupType.Active ||
                  groupType === GroupType.Inactive ||
                  groupType === GroupType.FullySold
                ) {
                  acc[i.id.toString()] = expanded;
                }
              });
            }
            return acc;
          },
          {} as Record<string, boolean>
        ) ?? {};

      setExpandedState(newState);
    },
    [listings, expandedState, setExpandedState]
  );

  const collapseGroupItems = useCallback(
    () => updateItemsState(false, false),
    [updateItemsState]
  );
  const collapseLeaderItems = useCallback(
    () => updateItemsState(false, true),
    [updateItemsState]
  );
  const expandLeaderItems = useEventCallback(() =>
    updateItemsState(true, true)
  );

  const staggerCollapse = useEventCallback(() => {
    const anyGroupExpanded = listings?.some(
      (l) =>
        l.isLtGrp &&
        (l as ListingGroup).groupItems.some(
          (i) =>
            ((i as ListingGroup).groupType === GroupType.Active ||
              (i as ListingGroup).groupType === GroupType.Inactive ||
              (i as ListingGroup).groupType === GroupType.FullySold) &&
            (expandedState as Record<string, boolean>)[i.id.toString()]
        )
    );

    if (anyGroupExpanded) {
      collapseGroupItems();
    } else {
      const anyLeaderExpanded = listings?.some(
        (l) =>
          l.isLtGrp &&
          (expandedState as Record<string, boolean>)[l.id.toString()]
      );

      if (anyLeaderExpanded) {
        collapseLeaderItems();
      }
    }
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isCommandMenuOpened) {
        return;
      }

      if (!hasExpandCollapseFeature) {
        return;
      }

      if (
        (event.metaKey || event.ctrlKey) &&
        ['[', ']'].includes(event.key.toLowerCase())
      ) {
        event.stopPropagation();
        event.preventDefault();
      }

      if ((event.metaKey || event.ctrlKey) && event.key === ']') {
        event.preventDefault();
        expandLeaderItems();
      } else if (
        (event.metaKey || event.ctrlKey) &&
        event.key === '[' &&
        listings?.some((l) => l.isLtGrp)
      ) {
        event.preventDefault();
        staggerCollapse();
      }
    };

    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () =>
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [
    collapseGroupItems,
    collapseLeaderItems,
    expandLeaderItems,
    staggerCollapse,
    expandedState,
    listings,
    hasExpandCollapseFeature,
    isCommandMenuOpened,
  ]);

  useEffect(() => {
    if (!hasExpandCollapseFeature) {
      return;
    }

    registerCommand({
      command: {
        id: CommandOptionType.ExpandAll,
        onSelect: expandLeaderItems,
      },
    });

    registerCommand({
      command: {
        id: CommandOptionType.CollapseAll,
        onSelect: staggerCollapse,
      },
    });

    return () => {
      if (!hasExpandCollapseFeature) {
        return;
      }

      unregisterCommand({ commandId: CommandOptionType.ExpandAll });
      unregisterCommand({ commandId: CommandOptionType.CollapseAll });
    };
  }, [
    hasExpandCollapseFeature,
    expandLeaderItems,
    registerCommand,
    staggerCollapse,
    unregisterCommand,
  ]);

  return {
    expandLeaderItems,
    collapseLeaderItems,
    collapseGroupItems,
  };
};
